import { addNavbarNotification } from "../utils/navbarNotifications";
import {
  loadAttendanceGeneralSettings,
  ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT,
  ATTENDANCE_SCHEDULE_RECALC_EVENT,
  dispatchAttendanceScheduleRecalc,
  getScheduleIntervalMs,
  scheduleLabel,
  setScheduleLastRunMs,
  shouldRunScheduledRecalc,
} from "./attendanceGeneralSettings";
import { ATTENDANCE_PUNCH_UPDATED_EVENT } from "./attendancePunchStorage";
import { startDeviceIntegrationPoller } from "./attendanceDeviceIntegration";

const TICK_MS = 60 * 1000;

let tickTimer = null;

function notifyScheduleRecalc(settings, { reason = "scheduled" } = {}) {
  const roles = settings?.notifyRoles || [];
  const label = scheduleLabel(settings?.runAttendanceSchedule);
  if (roles.length) {
    const reasonNote =
      reason === "manual_run"
        ? ", manual Run"
        : reason === "manual"
          ? ", saved settings"
          : "";
    addNavbarNotification({
      targetRoles: roles,
      title: "Attendance recalculated",
      message: `PR/LOP totals were refreshed (${label} schedule${reasonNote}).`,
      type: "attendance_schedule",
    });
  }
}

export function runAttendanceScheduleRecalc({ reason = "scheduled", notify = true } = {}) {
  const settings = loadAttendanceGeneralSettings();
  setScheduleLastRunMs();
  dispatchAttendanceScheduleRecalc({ reason, schedule: settings.runAttendanceSchedule });
  if (notify) notifyScheduleRecalc(settings, { reason });
}

let runnerStartedFlag = false;

/**
 * Global timer: non-realtime schedules fire batch PR/LOP recalc; realtime relies on punch events.
 */
export function startAttendanceScheduleRunner() {
  if (typeof window === "undefined" || runnerStartedFlag) return () => {};
  runnerStartedFlag = true;

  // Debounce punch→recalc so rapid status writes cannot storm the event bus.
  let punchRecalcTimer = null;
  const onPunch = () => {
    const { runAttendanceSchedule } = loadAttendanceGeneralSettings();
    if (runAttendanceSchedule !== "realtime") return;
    if (punchRecalcTimer) window.clearTimeout(punchRecalcTimer);
    punchRecalcTimer = window.setTimeout(() => {
      punchRecalcTimer = null;
      dispatchAttendanceScheduleRecalc({ reason: "punch", batch: false });
    }, 400);
  };

  const onSettingsSaved = (e) => {
    const schedule = e?.detail?.runAttendanceSchedule;
    if (schedule && schedule !== "realtime") {
      runAttendanceScheduleRecalc({ reason: "manual", notify: true });
    }
  };

  const tick = () => {
    const settings = loadAttendanceGeneralSettings();
    const { runAttendanceSchedule } = settings;
    if (runAttendanceSchedule === "realtime") return;
    if (shouldRunScheduledRecalc(runAttendanceSchedule)) {
      runAttendanceScheduleRecalc({ reason: "scheduled", notify: true });
    }
  };

  window.addEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
  window.addEventListener(ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT, onSettingsSaved);
  tickTimer = window.setInterval(tick, TICK_MS);
  tick();
  const stopDevices = startDeviceIntegrationPoller();

  return () => {
    if (stopDevices) stopDevices();
    runnerStartedFlag = false;
    if (punchRecalcTimer) {
      window.clearTimeout(punchRecalcTimer);
      punchRecalcTimer = null;
    }
    window.removeEventListener(ATTENDANCE_PUNCH_UPDATED_EVENT, onPunch);
    window.removeEventListener(
      ATTENDANCE_GENERAL_SETTINGS_UPDATED_EVENT,
      onSettingsSaved
    );
    if (tickTimer) window.clearInterval(tickTimer);
    tickTimer = null;
  };
}

export { ATTENDANCE_SCHEDULE_RECALC_EVENT, getScheduleIntervalMs };
